import { call, put } from "redux-saga/effects";
import {
    getAnnotationsReq,
    submitAnnotationsReq,
    submitIsAnnotationReviewedReq,
    removeAnnotationReq,
    submitVisualAnnotationsReq,
} from "api/annotations";
import { resetApiError, riseError } from "../general";
import {
    fetchAnnotations,
    submitAnnotations,
    submitIsAnnotationReviewed,
    removeAnnotation,
    attachAssetToAnnotation,
    removeAnnotationAsset,
} from "./annotations.actions";
import { Annotation, AnnotationModel } from "./interfaces";
import { buildAnnotationsApiModel } from "api/factories/annotations.factory";
import { AssetRes, deleteAssetReq, uploadAssetReq } from "api/assets";
import { Permission } from "api/creatives";
import { updateExistingSpecProperties, updateExistingSpecPropertiesSaga } from "store/features/materialSpecs/specs";

export function* fetchAnnotationsSaga(
    headers: HeadersInit,
    { payload: { matSpecId, adMaterialId, assetId } }: ReturnType<typeof fetchAnnotations.request>
): Generator {
    try {
        const annotationsModel = (yield call(
            getAnnotationsReq,
            headers,
            matSpecId,
            adMaterialId,
            assetId
        )) as AnnotationModel;

        yield put(
            fetchAnnotations.success({
                adMaterialId,
                annotations: annotationsModel.annotations,
                permissions: annotationsModel.permissions,
            })
        );
    } catch (err) {
        if (err.status) {
            yield put(fetchAnnotations.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* submitAnnotationsSaga(
    headers: HeadersInit,
    {
        payload: { materialSpecId, adMaterialId, action, annotations, assetId },
    }: ReturnType<typeof submitAnnotations.request>
): Generator {
    let updatedAnnotationsModel: { permissions: Permission[]; annotations: Annotation[] };

    try {
        if (!assetId) {
            updatedAnnotationsModel = (yield call(
                submitAnnotationsReq,
                headers,
                adMaterialId,
                action,
                buildAnnotationsApiModel(annotations)
            )) as AnnotationModel;
        } else {
            updatedAnnotationsModel = (yield call(
                submitVisualAnnotationsReq,
                headers,
                assetId,
                adMaterialId,
                action,
                buildAnnotationsApiModel(annotations)
            )) as AnnotationModel;
        }

        const { annotations: updatedAnnotations, permissions } = updatedAnnotationsModel;

        yield put(
            submitAnnotations.success({
                permissions,
                annotations: updatedAnnotations,
            })
        );
        yield call(updateExistingSpecPropertiesSaga, headers, updateExistingSpecProperties.request({ materialSpecId }));
    } catch (err) {
        if (err.status) {
            yield put(fetchAnnotations.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* submitIsAnnotationReviewedSaga(
    headers: HeadersInit,
    { payload: { id, reviewed } }: ReturnType<typeof submitIsAnnotationReviewed.request>
): Generator {
    try {
        yield call(submitIsAnnotationReviewedReq, headers, id, reviewed);
        yield put(submitIsAnnotationReviewed.success(null));
    } catch (err) {
        if (err.status) {
            yield put(submitIsAnnotationReviewed.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* removeAnnotationSaga(
    headers: HeadersInit,
    { payload: { annotationId } }: ReturnType<typeof removeAnnotation.request>
): Generator {
    try {
        yield call(removeAnnotationReq, headers, annotationId);
        yield put(removeAnnotation.success(null));
    } catch (err) {
        if (err.status) {
            yield put(fetchAnnotations.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* attachAssetToAnnotationSaga(
    headers: HeadersInit,
    {
        payload: { materialSpecificationId, adMaterialId, selectionId, asset, assetType },
    }: ReturnType<typeof attachAssetToAnnotation.request>
): Generator {
    try {
        const formData = new FormData();
        formData.append("matspec", materialSpecificationId.toString());
        formData.append("admaterial", adMaterialId.toString());
        formData.append("type", assetType);
        formData.append("file", asset);

        // server returns an array of assets. in this case, we will always get only one
        const [uploadedAsset] = (yield call(uploadAssetReq, headers, formData)) as AssetRes[];
        yield put(
            attachAssetToAnnotation.success({
                id: uploadedAsset.id,
                selectionId,
                fileName: asset.name,
            })
        );
    } catch (err) {
        if (err.status) {
            yield put(attachAssetToAnnotation.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}

export function* removeAnnotationAssetSaga(
    headers: HeadersInit,
    { payload: { selectionId, assetId } }: ReturnType<typeof removeAnnotationAsset.request>
): Generator {
    try {
        yield call(deleteAssetReq, headers, assetId);
        yield put(removeAnnotationAsset.success({ selectionId }));
    } catch (err) {
        if (err.status) {
            yield put(removeAnnotationAsset.failure({ statusCode: err.status }));
            yield put(resetApiError());

            return;
        }

        yield put(riseError(err));
    }
}
